/* #region(collapsed) Developer Documentation ══════════════════════════════════════════════ */
/*
  @license
  # COPYRIGHT NOTICE:
    © CROUIN 2021. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  # INTRODUCTION:
    Related to ./sections/media-text-maxdesign.liquid
    This module features mostly rely on HTML custom elements: media-text-maxesign, media-text-video-maxdesign.

  # MORE INFORMATION:
    - Contact us at admin(at)maxdesign.expert
*/
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) MEDIA TEXT ═══════════════════════════════════════════════════════════ */
class MediaText extends HTMLElement {
  connectedCallback() {
    this.settings = this.json(this);
    this.section = this.closest('.shopify-section');
    if (this.settings.slider.enable) this.initSlider();
    if (this.settings.animations.wheel || this.settings.animations.number) this.initAnimations();
    if (this.settings.klaviyo.enable) this.onKlaviyoSubmit();
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Slider Methods */

  /**
   * Init slider
   * setSlideFlexBasis is called when element is resized and when video metadata is ready (may change the video dimensions)
   * Loop is not enabled because of issue https://github.com/davidjerleke/embla-carousel/issues/1045
   */
  async initSlider() {
    if (typeof EmblaCarousel === 'undefined') await this.loadEmbla(this.settings.slider.embla);
    this.viewport = this.querySelector('.mtmd__slider');
    this.slides = this.querySelectorAll('.mtmd__slide-container > *:first-child');
    const options = {
      slidesToScroll: 'auto',
      containScroll: 'trimSnaps',
      inViewThreshold: 0.8,
    };
    const plugins = [EmblaCarouselClassNames()];
    this.slider = EmblaCarousel(this.viewport, options, plugins);
    this.section.addEventListener('maxdesign:video:loadedmetadata', this.setSlideFlexBasis.bind(this), { once: true });
    new ResizeObserver(this.setSlideFlexBasis.bind(this)).observe(this);
    if (!this.mobile) this.addCursorNav();
    else this.initSliderNav();
  }

  /**
   * Scale slide to fit section height
   * Do not scale up if only one slide is overflowing by 33% or less
   */
  setSlideFlexBasis(e) {
    if (this.mobile || this.sliderPosDesktop == 'auto') return;
    this.style.removeProperty('--slide-flex-desktop');
    const { width: slideWidth, height: slideHeight } = this.slides[0].getBoundingClientRect();
    const viewPortWidth = this.viewport.firstElementChild.offsetWidth;
    const heightTarget = this.getBoundingClientRect().height;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--slider-gap')) || 0;
    const flexBasis = Math.floor((heightTarget * slideWidth) / slideHeight + gap);
    const totalWidth = this.slider.slideNodes().length * flexBasis;
    const oneThirdOverflow = totalWidth - viewPortWidth > flexBasis / 3;
    if (oneThirdOverflow) this.style.setProperty('--slide-flex-desktop', `${flexBasis}px`);
  }

  /**
   * Adds left/right arrow cursor effect on the carousel viewport
   */
  addCursorNav() {
    const rootStyles = getComputedStyle(this.section);
    const leftArrow = rootStyles.getPropertyValue('--slide-cursor-arrow-left').trim();
    const rightArrow = rootStyles.getPropertyValue('--slide-cursor-arrow-right').trim();
    const LEFT_ZONE = 0.2;
    const RIGHT_ZONE = 0.8;
    let cursorZone = null,
      lastX = null;

    const setCursor = x => {
      const rect = this.viewport.getBoundingClientRect();
      const canPrev = this.slider.canScrollPrev();
      const canNext = this.slider.canScrollNext();
      if (x === null) {
        this.viewport.style.removeProperty('cursor');
        cursorZone = null;
      } else if (x < rect.width * LEFT_ZONE && canPrev) {
        this.viewport.style.cursor = leftArrow;
        cursorZone = 'left';
      } else if (x > rect.width * RIGHT_ZONE && canNext) {
        this.viewport.style.cursor = rightArrow;
        cursorZone = 'right';
      } else {
        this.viewport.style.removeProperty('cursor');
        cursorZone = null;
      }
    };

    this.viewport.addEventListener('mousemove', e => {
      lastX = e.clientX - this.viewport.getBoundingClientRect().left;
      setCursor(lastX);
    });

    this.viewport.addEventListener('click', e => {
      if (cursorZone === 'left' && this.slider.canScrollPrev()) this.slider.scrollPrev();
      else if (cursorZone === 'right' && this.slider.canScrollNext()) this.slider.scrollNext();
    });

    this.slider.on('select', () => setCursor(lastX));
  }

  /**
   * Nav in mobile with scroll progress indicator
   */
  initSliderNav() {
    const nav = this.querySelector('.mtmd__slider-nav');
    if (!nav) return;

    const prevButton = nav.querySelector('.mtmd__arrow--prev');
    const nextButton = nav.querySelector('.mtmd__arrow--next');

    prevButton?.addEventListener('click', this.slider.scrollPrev.bind(this));
    nextButton?.addEventListener('click', this.slider.scrollNext.bind(this));

    const update = () => {
      if (prevButton) prevButton.disabled = !this.slider.canScrollPrev();
      if (nextButton) nextButton.disabled = !this.slider.canScrollNext();
      this.scrollProgress = this.slider.scrollProgress();
    };

    this.slider.on('select', update);
    this.slider.on('scroll', () => (this.scrollProgress = this.slider.scrollProgress()));

    update();
  }

  /**
   * Load embla library
   */
  async loadEmbla(src) {
    await new Promise((resolve, reject) => {
      this.parentElement.appendChild(
        Object.assign(document.createElement('script'), {
          src: src,
          onload: resolve,
          onerror: reject,
        })
      );
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Animations Methods */
  initAnimations() {
    this.triggers = this.querySelectorAll('.mtmd__number');
    this.intersectionObserver = new IntersectionObserver(this.progressAnimate.bind(this), { rootMargin: '10px' });
    this.triggers.forEach(trigger => {
      const value = trigger.firstElementChild.textContent.match(/\d+(?:[,. ]\d+)*/);
      trigger.dataset.value = value;
      trigger.style.setProperty('--value', value);
      this.intersectionObserver.observe(trigger);
    });
  }

  /**
   * Trigger progress animation
   * @param {Object} entries - block intersecting entries
   */
  progressAnimate(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.dataset.intersected = true;
        if (entry.target.hasAttribute('data-number-animate')) this.textAnimate(entry.target);
        this.intersectionObserver.unobserve(entry.target);
      }
    });
  }

  textAnimate(target) {
    const node = target.firstElementChild;
    const text = node.innerHTML;
    const endValue = target.dataset.value;
    if (isNaN(endValue)) return;
    var startValue = 0;
    const duration = Math.floor(1300 / endValue);
    const counter = setInterval(() => {
      startValue += 1;
      node.innerHTML = text.replace(/\d+(?:[,. ]\d+)*/, startValue);
      if (startValue == endValue) clearInterval(counter);
    }, duration);
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Klaviyo Methods */
  onKlaviyoSubmit() {
    this.newsletter = {
      form: this.querySelector('.mtmd__newsletter'),
      input: this.querySelector('.mtmd__newsletter input'),
      success: this.querySelector('.mtmd__newsletter-success'),
      error: this.querySelector('.mtmd__newsletter-error'),
    };
    this.newsletter.form.addEventListener('submit', e => {
      e.preventDefault();
      const endpoint = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.settings.klaviyo.publicAPI}`;
      const email = this.newsletter.input.value;
      const options = this.options(email, 'subscription');
      (async () => {
        try {
          const response = await fetch(endpoint, options);
          this.newsletter.success.hidden = !response.ok;
          this.newsletter.error.hidden = response.ok;
          if (response.ok) this.updateProfile(email);
          else throw await response.json();
        } catch (error) {
          const errorMsg = error.errors?.map(err => err.detail)?.join(' ') || 'An unknown error occurred';
          this.newsletter.error.querySelector('[data-form-error]').textContent = errorMsg;
        }
      })();
    });
  }

  /**
   * Update profile custom property such as localization
   * @param {String} email - person's email
   */
  updateProfile(email) {
    const endpoint = `https://a.klaviyo.com/client/profiles/?company_id=${this.settings.klaviyo.publicAPI}`;
    const options = this.options(email, 'profile');
    fetch(endpoint, options).catch(console.error);
  }

  get options() {
    return (email, type) => ({
      method: 'POST',
      headers: { accept: 'application/json', revision: '2024-06-15', 'content-type': 'application/json' },
      body: JSON.stringify({
        data: {
          type,
          attributes:
            type == 'profile'
              ? { email, properties: { Localization: `${Shopify.locale}-${Shopify.country}` } }
              : {
                  custom_source: this.settings.klaviyo.source,
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
          ...(type == 'subscription' && {
            relationships: {
              list: { data: { type: 'list', id: this.settings.klaviyo.listId } },
            },
          }),
        },
      }),
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */

  /**
   * @returns {Object} custom element settings
   */
  get json() {
    return scope => JSON.parse(scope.querySelector('[type="application/json"]')?.textContent);
  }

  get mobile() {
    return matchMedia('(max-width:1024px)').matches;
  }

  get sliderPosDesktop() {
    return this.dataset.sliderPosition.split('/')[1];
  }

  set scrollProgress(progress) {
    const n = this.slider.scrollSnapList().length || this.slider.slideNodes().length;
    const raw = Math.max(0, Math.min(1, progress || 0));
    const adjusted = n > 1 ? (raw * (n - 1) + 1) / n : 1;
    this.style.setProperty('--scroll-progress', adjusted.toFixed(6));
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) VIDEO MEDIA ═══════════════════════════════════════════════════════════ */
class Video extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.viewport = this.closest('.mtmd__slider');
    this.slide = this.closest('.mtmd__slide');
    this.video = this.querySelector('video');
    this.player = this.querySelector('button[data-player-btn]');
    this.muter = this.querySelector('button[data-muter-btn]');
    // Add listeners for play and pause events
    this.section.addEventListener('maxdesign:video:playing', this.onDelegatedPlaying.bind(this));
    this.video.addEventListener('loadedmetadata', this.onLoadedmetadata.bind(this));
    this.video.addEventListener('play', this.onVideoPlaying.bind(this));
    this.video.addEventListener('pause', this.onVideoPausing.bind(this));
    this.player.addEventListener('click', this.onPlayerToggle.bind(this));
    this.muter.addEventListener('click', this.onMuterToggle.bind(this));
    // Lazyload observer
    this.lazyLoadObserver = new IntersectionObserver(this.onLazyLoad.bind(this), { rootMargin: '750px' });
    this.lazyLoadObserver.observe(this.video);
    // MutationObserver to monitor slide class changes
    this.mutationObserver = new MutationObserver(e => {
      const play = this.canAutoplay && this.isSnapped && !this.isDragging;
      if (play !== this.playing) this.play = play;
    });
    this.mutationObserver.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) User Events */
  onLazyLoad([entry]) {
    if (!entry.isIntersecting) return;
    this.loadPoster();
    this.video.querySelectorAll('source[data-src]').forEach(source => (source.src = source.dataset.src));
    this.video.load();
    this.dataset.loaded = 'true';
    this.play = this.canAutoplay;
    this.lazyLoadObserver.unobserve(this.video);
    new IntersectionObserver(this.onLazyPlay.bind(this)).observe(this.video);
  }

  onLazyPlay([entry]) {
    this.play = entry.isIntersecting ? this.canAutoplay : false;
  }

  onVideoPlaying(e) {
    this.setAttribute('data-playing', true);
    this.video.controls = this.dataset.controls == 'all';
    this.section.dispatchEvent(new CustomEvent('maxdesign:video:playing', { detail: this }));
  }

  onVideoPausing() {
    this.setAttribute('data-playing', false);
  }

  onPlayerToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (!this.playing) {
      this.slideIntoView();
      this.mute = !this.canUnmute;
      this.play = true;
    } else {
      this.play = false;
    }
  }

  onMuterToggle() {
    this.mute = !this.muted;
    this.play = true;
  }

  /**
   * Manage video player when another video is playing
   * Play video only if it's already autoplaying, otherwise pause
   * Mute video every time
   * We can load all posters from same medium that have not yet interesected at this point
   * @param {Object} e - custom video:playing event
   */
  onDelegatedPlaying(e) {
    if (e.detail == this) return;
    this.play = this.canAutoplay && this.isSnapped && !this.isDragging;
    if (e.detail.closest('.mtmd__slide')?.dataset?.medium == this.slide?.dataset?.medium) this.loadPoster();
  }

  /**
   * Small delay to ensure the video dimensions are accurate
   */
  onLoadedmetadata() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.section.dispatchEvent(new CustomEvent('maxdesign:video:loadedmetadata'));
      });
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Auxiliary Methods */

  loadPoster() {
    const posterUrl = this.video.getAttribute('data-poster');
    if (posterUrl) {
      this.video.poster = posterUrl.replace(/(\d+)x(?=\.\w+)/, `${Math.round(this.offsetWidth)}x`);
      this.removeAttribute('data-poster');
    }
  }

  fallbackPlayer(error) {
    console.error('Autoplay failed:', error);
    this.setAttribute('data-playing', false);
    this.setAttribute('data-autoplay-rejected', '');
    this.video.addEventListener(
      'click',
      () => {
        this.mute = !this.canUnmute;
        this.play = true;
      },
      { once: true }
    );
  }

  slideIntoView() {
    const slide = this.closest('.mtmd__slide');
    if (!slide || !this.slider) return;

    const domIndex = this.slider.slideNodes().indexOf(slide);
    const index = this.slider.internalEngine().slideRegistry.findIndex(group => group.includes(domIndex));

    if (index > -1) this.slider.scrollTo(index);
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */
  get slider() {
    return (this._slider = this._slider || this.closest('.mtmd')?.slider);
  }
  get playing() {
    return !!(this.video.currentTime > 0 && !this.video.paused && !this.video.ended && this.video.readyState > 2);
  }
  get muted() {
    return this.video.hasAttribute('muted');
  }
  get canAutoplay() {
    if (!this.video.autoplay) return false;
    const isGridBanner = this.closest('.mtmd__banner');
    const isSliderEnable = this.closest('.mtmd').settings.slider.enable;
    if (isGridBanner || !isSliderEnable) return true;
    return this.isSnapped && this.isInView;
  }
  get canUnmute() {
    return this.dataset.controls != 'none';
  }
  get isInView() {
    return this.slide.classList.contains('is-in-view');
  }
  get isSnapped() {
    return this.slide.classList.contains('is-snapped');
  }
  get isDragging() {
    return this.viewport.classList.contains('is-dragging');
  }
  set play(play) {
    if (play)
      this.video
        .play()
        .then(this.removeAttribute.bind(this, 'data-autoplay-rejected'))
        .catch(this.fallbackPlayer.bind(this));
    else this.video.pause();
  }

  set mute(mute) {
    this.video.toggleAttribute('muted', mute);
    this.video.muted = mute;
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

customElements.define('media-text-maxdesign', MediaText);
customElements.define('media-text-video-maxdesign', Video);
